import React, { useState } from 'react';
import { Card, Form, Input, Button, Space, Tag, Select, message } from 'antd';
import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './index.css';

const { TextArea } = Input;
const { Option } = Select;

const WritePost = ({ isEditMode = false }) => {
  const [form] = Form.useForm();
  const [tags, setTags] = useState(isEditMode ? ['React', '教程'] : []);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (values) => {
    setLoading(true);
    
    try {
      const articleData = {
        ...values,
        tags: tags,
        id: isEditMode ? values.id : Date.now(), // 如果是编辑模式，使用原有ID，否则生成新ID
        author: '当前用户', // 这里可以从登录用户信息获取
        date: new Date().toLocaleDateString('zh-CN'),
        views: 0,
        likes: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // 保存到 localStorage（模拟数据库）
      const existingPosts = JSON.parse(localStorage.getItem('blog_posts') || '[]');
      
      if (isEditMode) {
        // 编辑文章：找到并更新
        const index = existingPosts.findIndex(post => post.id === articleData.id);
        if (index !== -1) {
          existingPosts[index] = { ...existingPosts[index], ...articleData };
          localStorage.setItem('blog_posts', JSON.stringify(existingPosts));
          message.success('文章更新成功！');
        }
      } else {
        // 添加新文章
        const newPosts = [articleData, ...existingPosts];
        localStorage.setItem('blog_posts', JSON.stringify(newPosts));
        message.success('文章发布成功！');
        
        // 重置表单
        form.resetFields();
        setTags([]);
      }
      
      // 跳转到文章详情页或首页
      setTimeout(() => {
        if (isEditMode) {
          navigate(`/post/${articleData.id}`);
        } else {
          navigate('/');
        }
      }, 1000);
      
    } catch (error) {
      console.error('保存文章失败:', error);
      message.error('保存文章失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (form.isFieldsTouched()) {
      // 如果有未保存的内容，提示确认
      if (window.confirm('确定要放弃未保存的内容吗？')) {
        navigate(-1); // 返回上一页
      }
    } else {
      navigate(-1);
    }
  };

  const handleTagClose = (removedTag) => {
    const newTags = tags.filter(tag => tag !== removedTag);
    setTags(newTags);
  };

  const tagOptions = ['React', 'Vue', 'JavaScript', 'TypeScript', 'Node.js', '前端', '后端', '教程', '实战'];

  return (
    <div className="write-post-container">
      <Card 
        title={isEditMode ? "编辑文章" : "撰写新文章"} 
        className="write-post-card"
        extra={
          <Space>
            <Button 
              icon={<CloseOutlined />} 
              onClick={handleCancel}
              disabled={loading}
            >
              取消
            </Button>
            <Button 
              type="primary" 
              icon={<SaveOutlined />}
              onClick={() => form.submit()}
              loading={loading}
            >
              {isEditMode ? '更新文章' : '发布文章'}
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="write-post-form"
          initialValues={isEditMode ? {
            title: 'React入门教程',
            summary: '学习React的基础知识',
            content: '# React入门教程\n\nReact是一个用于构建用户界面的JavaScript库。'
          } : {}}
        >
          <Form.Item
            label="文章标题"
            name="title"
            rules={[{ required: true, message: '请输入文章标题' }]}
          >
            <Input 
              placeholder="请输入文章标题" 
              size="large"
              maxLength={100}
              showCount
            />
          </Form.Item>

          <Form.Item
            label="文章摘要"
            name="summary"
            rules={[
              { required: true, message: '请输入文章摘要' },
              { max: 200, message: '摘要不能超过200字' }
            ]}
          >
            <TextArea 
              placeholder="请输入文章摘要，不超过200字" 
              rows={3}
              showCount 
              maxLength={200}
            />
          </Form.Item>

          <Form.Item label="标签">
            <div className="tags-section">
              <div className="tags-container">
                {tags.map(tag => (
                  <Tag
                    key={tag}
                    closable
                    onClose={() => handleTagClose(tag)}
                    className="article-tag"
                  >
                    {tag}
                  </Tag>
                ))}
              </div>
              
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="选择或输入标签（按回车键创建新标签）"
                value={tags}
                onChange={setTags}
                dropdownRender={menu => (
                  <div>
                    {menu}
                    <div style={{ padding: 8, fontSize: '12px', color: '#999' }}>
                      输入标签后按回车添加
                    </div>
                  </div>
                )}
              >
                {tagOptions.map(tag => (
                  <Option key={tag} value={tag}>
                    {tag}
                  </Option>
                ))}
              </Select>
            </div>
          </Form.Item>

          <Form.Item
            label="文章内容（支持Markdown语法）"
            name="content"
            rules={[{ required: true, message: '请输入文章内容' }]}
          >
            <TextArea 
              placeholder="请使用 Markdown 格式输入文章内容" 
              rows={15}
              className="markdown-editor"
            />
          </Form.Item>

          <div className="markdown-hint">
            <h4>Markdown 语法提示：</h4>
            <div className="hint-grid">
              <div className="hint-item">
                <code># 标题</code>
                <span>一级标题</span>
              </div>
              <div className="hint-item">
                <code>## 标题</code>
                <span>二级标题</span>
              </div>
              <div className="hint-item">
                <code>**文本**</code>
                <span>加粗</span>
              </div>
              <div className="hint-item">
                <code>*文本*</code>
                <span>斜体</span>
              </div>
              <div className="hint-item">
                <code>- 项目</code>
                <span>无序列表</span>
              </div>
              <div className="hint-item">
                <code>1. 项目</code>
                <span>有序列表</span>
              </div>
              <div className="hint-item">
                <code>`代码`</code>
                <span>行内代码</span>
              </div>
              <div className="hint-item">
                <pre>```代码块```</pre>
                <span>代码块</span>
              </div>
              <div className="hint-item">
                <code>[链接](url)</code>
                <span>超链接</span>
              </div>
              <div className="hint-item">
                <code>![图片](url)</code>
                <span>插入图片</span>
              </div>
            </div>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default WritePost;